import React, { useEffect, useRef, useState } from "react";
import {
  StyledExpandContainer,
  StyledExpandMenu,
  StyledExpandMenuItem,
  StyledExpandMenuItemsContainer,
  StyledExpandMenuItemWrapper,
  StyledUnreadMarkWrapper,
} from "./ExpandMenu.styled";
import { BodyTyp } from "../Typography/Typography";
import { theme } from "../../constants/StyledComponents";
import { FontWeightEnum, IconSizeEnum } from "../../types/AppTypes";
import { UnReadMark } from "../UnReadMark/UnReadMark";
import { ReactComponent as ListIcon } from "../../icons/list.svg";
import { ReactComponent as CloseIcon } from "../../icons/close.svg";
import { IconManager } from "../core/IconManager/IconManager";
import { Tooltip, TooltipVariantEnum } from "../core/Tooltip/Tooltip";
import { TooltipPositionEnum } from "../core/Tooltip/Tooltip.style";

export type ExpandMenuOptionItem<T> = {
  value: T;
  label: string;
  disabled?: boolean;
  hasNotification?: boolean;
};

type ExpandMenuProps<T> = {
  menuSelected: ExpandMenuOptionItem<T>;
  setMenuSelected: (x: ExpandMenuOptionItem<T>) => void;
  data: ExpandMenuOptionItem<T>[];
  confirmLeave?: (action: () => void) => void;
};

export const ExpandMenu = <T,>({
  menuSelected,
  setMenuSelected,
  data,
  confirmLeave,
}: ExpandMenuProps<T>) => {
  const [hoveredItem, setHoveredItem] =
    useState<ExpandMenuOptionItem<T> | null>(null);
  const [expanded, setExpanded] = useState(false);

  const handleSwitchMenu = (item: ExpandMenuOptionItem<T>) => {
    if (item.disabled) {
      return undefined;
    }

    confirmLeave
      ? confirmLeave(() => setMenuSelected(item))
      : setMenuSelected(item);
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <StyledExpandMenu $expanded={expanded} ref={wrapperRef}>
      <StyledExpandContainer $expanded={expanded}>
        <IconManager
          size={IconSizeEnum.MEDIUM}
          color={theme.colors.purple}
          onClick={() => setExpanded(!expanded)}
          id="expand-menu-icon"
        >
          {expanded ? <CloseIcon /> : <ListIcon />}
        </IconManager>
        <StyledExpandMenuItemsContainer>
          {data.map((item, idx) => {
            const isHighlighted =
              menuSelected.value === item.value ||
              hoveredItem?.value === item.value;

            let fontWeight = FontWeightEnum.REGULAR;
            if (!item.disabled && isHighlighted) {
              fontWeight = FontWeightEnum.SEMIBOLD;
            }

            let color = theme.colors.purple;
            if (item.disabled) {
              color = theme.colors.grey_3;
            } else if (isHighlighted) {
              color = theme.colors.white;
            }

            const labelText = expanded
              ? `${idx + 1} ${item.label}`
              : `${idx + 1}`;

            const menuItem = (
              <StyledExpandMenuItem
                onClick={() => handleSwitchMenu(item)}
                $active={menuSelected.value === item.value}
                $disabled={item.disabled}
                onMouseEnter={() => setHoveredItem(item)}
                onMouseLeave={() => setHoveredItem(null)}
                $expanded={expanded}
              >
                <BodyTyp fontWeight={fontWeight} color={color}>
                  {labelText}
                </BodyTyp>
              </StyledExpandMenuItem>
            );

            return (
              <StyledExpandMenuItemWrapper key={`${item.value}${idx}`}>
                {expanded ? (
                  menuItem
                ) : (
                  <Tooltip
                    message={item.label}
                    variant={TooltipVariantEnum.DARK}
                    position={TooltipPositionEnum.RIGHT}
                    autoWidth
                  >
                    {menuItem}
                  </Tooltip>
                )}
                <StyledUnreadMarkWrapper
                  $visible={item.hasNotification ?? false}
                >
                  <UnReadMark size="medium" visible />
                </StyledUnreadMarkWrapper>
              </StyledExpandMenuItemWrapper>
            );
          })}
        </StyledExpandMenuItemsContainer>
      </StyledExpandContainer>
    </StyledExpandMenu>
  );
};
